import { useLocale } from "../hooks/useLocale";
import type { StepItem } from "../store/chat";
import { formatStepLabel } from "../utils/stepLabel";
import styles from "./StepTimeline.module.css";

interface Props {
  steps: StepItem[];
  active?: boolean;
  collapsible?: boolean;
}

function statusGlyph(status: StepItem["status"]): string {
  switch (status) {
    case "running":
      return "•";
    case "done":
      return "✓";
    case "error":
      return "✕";
  }
}

export function StepTimeline({ steps, active = false, collapsible = false }: Props) {
  const { t } = useLocale();
  if (steps.length === 0) {
    return active ? <p className={styles.thinking}>{t("chat.thinking")}</p> : null;
  }

  // Show the "thinking" indicator after the list whenever the agent is still
  // active and the last step is finished — keeps the user oriented during the
  // LLM-thinking gap between tool calls (otherwise the timeline looks idle).
  const lastStatus = steps[steps.length - 1]?.status;
  const showThinking = active && lastStatus !== "running";

  const list = (
    <ol className={styles.list}>
      {steps.map((step) => (
        <li key={step.index} className={styles[step.status]}>
          <span className={styles.glyph}>{statusGlyph(step.status)}</span>
          <span className={styles.tool}>{formatStepLabel(step.tool, step.toolInput, t)}</span>
          {step.durationMs !== undefined && (
            <span className={styles.duration}>{step.durationMs} ms</span>
          )}
        </li>
      ))}
    </ol>
  );

  if (!collapsible) {
    return (
      <div className={styles.box}>
        <span className={styles.title}>{t("chat.steps_title")}</span>
        {list}
        {showThinking && <p className={styles.thinking}>{t("chat.thinking")}</p>}
      </div>
    );
  }

  return (
    <details className={styles.box}>
      <summary className={styles.title}>
        {t("chat.steps_title")} · {steps.length}
      </summary>
      {list}
      {showThinking && <p className={styles.thinking}>{t("chat.thinking")}</p>}
    </details>
  );
}
